import * as DateF from 'date-fns'
import { useMemo } from 'react'
import styled from 'styled-components'

import { Duration } from '@blade/std-lib'
import { DurationFormatter, Tooltip } from '@blade/ui-kit'
import { formatDurationShortLocalized } from '@root/shared'
import { useTranslation } from '@root/shared/localization'

/**
 * How long ago something happened.
 *
 * The largest unit only, and short with it: thirty rows read as a sequence
 * when the time column is a glance rather than a sentence. The exact moment
 * is one hover away, which is where it belongs — needed rarely, and it would
 * cost the column three times its width to be always on.
 *
 * `now` is passed in rather than read here so that every row in a list shares
 * one moment, and so that nothing reads a clock while rendering.
 */
type Props = {
  /** Unix milliseconds. */
  at: number
  now: Date
}

export const WalletProfileAgo: React.FC<Props> = ({ at, now }) => {
  const { t } = useTranslation()

  const ago = useMemo(() => {
    const elapsed = Math.max(now.getTime() - at, 0)
    const unit = DurationFormatter.getLargestDurationUnit(
      DateF.intervalToDuration({ start: 0, end: elapsed }),
    )
    return formatDurationShortLocalized(
      Duration.millis(elapsed),
      t,
      unit ? [unit] : ['seconds'],
    )
  }, [at, now, t])

  const exact = useMemo(
    () => DateF.format(new Date(at), 'd MMM yyyy, HH:mm:ss'),
    [at],
  )

  return (
    <Tooltip content={exact}>
      <Ago>{ago}</Ago>
    </Tooltip>
  )
}

const Ago = styled.span`
  overflow: hidden;

  color: var(--text-9);
  font-size: 10px;
  white-space: nowrap;
`
